import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Download, FileSpreadsheet, ChevronDown, Truck, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const todayISO = () => new Date().toISOString().slice(0, 10);

// --- Lightweight multi-select (popover + checkboxes) ---
function MultiSelect({ label, options, selected, onChange, testid }) {
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
    else onChange([...selected, val]);
  };
  const summary = selected.length === 0 ? `All ${label}` : `${selected.length} selected`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between font-normal text-slate-700"
          data-testid={testid}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0 max-h-72 overflow-y-auto" align="start">
        <div className="p-2 flex items-center justify-between border-b border-slate-100">
          <span className="text-xs font-mono uppercase text-slate-500">{label}</span>
          {selected.length > 0 && (
            <button
              className="text-xs text-slate-500 hover:text-slate-900"
              onClick={() => onChange([])}
              data-testid={`${testid}-clear`}
            >
              Clear
            </button>
          )}
        </div>
        <div className="p-1">
          {options.length === 0 && (
            <p className="text-xs text-slate-400 px-2 py-3">No options available</p>
          )}
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
            >
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                data-testid={`${testid}-opt-${opt.value}`}
              />
              <span className="text-sm text-slate-700 truncate">{opt.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const FieldLabel = ({ children }) => (
  <label className="block text-[11px] font-mono uppercase tracking-wide text-slate-500 mb-1.5">{children}</label>
);

export default function Reports() {
  const [resource, setResource] = useState('equipment');
  const [reportType, setReportType] = useState('utilization');
  const [options, setOptions] = useState({ equipment: {}, personnel: {}, projects: [] });
  const [loading, setLoading] = useState(false);

  // Utilization filters
  const [uTimeframe, setUTimeframe] = useState('year');
  const [uCategory, setUCategory] = useState('all');
  const [uSubcats, setUSubcats] = useState([]);
  const [uProjects, setUProjects] = useState([]);
  const [uOwner, setUOwner] = useState('all');

  // Trend filters
  const [tTimeframe, setTTimeframe] = useState('year');
  const [tBucket, setTBucket] = useState('month');
  const [tCategory, setTCategory] = useState('all');
  const [tSubcats, setTSubcats] = useState([]);
  const [tOwner, setTOwner] = useState('all');
  const [tCountry, setTCountry] = useState('all');

  const [anchor, setAnchor] = useState(todayISO());

  const [utilData, setUtilData] = useState(null);
  const [trendData, setTrendData] = useState(null);

  const ro = options[resource] || {};
  const subLabel = resource === 'equipment' ? 'Subcategories' : 'Roles';
  const catLabel = resource === 'equipment' ? 'Category' : 'Department';
  const ownerLabel = resource === 'equipment' ? 'Owner' : 'Employer';
  const subOptions = (resource === 'equipment' ? ro.subcategories : ro.roles) || [];
  const catOptions = (resource === 'equipment' ? ro.categories : ro.departments) || [];
  const ownerOptions = ro.owners || [];
  const countryOptions = ro.countries || [];

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/reports/filter-options`, { headers: getAuthHeader() });
        setOptions(res.data);
      } catch {
        toast.error('Failed to load report filters');
      }
    })();
  }, []);

  // Reset resource-specific selections when resource changes
  useEffect(() => {
    setUCategory('all'); setUSubcats([]); setUOwner('all');
    setTCategory('all'); setTSubcats([]); setTOwner('all'); setTCountry('all');
  }, [resource]);

  const fetchUtilization = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ resource, timeframe: uTimeframe, anchor });
      if (uCategory !== 'all') params.set('category', uCategory);
      if (uSubcats.length) params.set('subcategories', uSubcats.join(','));
      if (uProjects.length) params.set('projects', uProjects.join(','));
      if (uOwner !== 'all') params.set('owner', uOwner);
      const res = await axios.get(`${API}/reports/utilization?${params}`, { headers: getAuthHeader() });
      setUtilData(res.data);
    } catch {
      toast.error('Failed to load utilization report');
    } finally {
      setLoading(false);
    }
  }, [resource, uTimeframe, anchor, uCategory, uSubcats, uProjects, uOwner]);

  const fetchTrend = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ resource, timeframe: tTimeframe, bucket: tBucket, anchor });
      if (tCategory !== 'all') params.set('category', tCategory);
      if (tSubcats.length) params.set('subcategories', tSubcats.join(','));
      if (tOwner !== 'all') params.set('owner', tOwner);
      if (tCountry !== 'all') params.set('country', tCountry);
      const res = await axios.get(`${API}/reports/trend?${params}`, { headers: getAuthHeader() });
      setTrendData(res.data);
    } catch {
      toast.error('Failed to load trend report');
    } finally {
      setLoading(false);
    }
  }, [resource, tTimeframe, tBucket, anchor, tCategory, tSubcats, tOwner, tCountry]);

  useEffect(() => {
    if (reportType === 'utilization') fetchUtilization();
    else fetchTrend();
  }, [reportType, fetchUtilization, fetchTrend]);

  const exportData = (format) => {
    let rows = [];
    let name = '';
    if (reportType === 'utilization' && utilData) {
      name = `utilisation_${resource}_${utilData.window_start}_${utilData.window_end}`;
      rows = utilData.rows.map((r) => ({
        [subLabel.replace(/s$/, '')]: r.label,
        Available: r.available,
        'Allocated (avg)': r.allocated,
        'Usage Rate %': r.usage_rate,
        Variance: r.variance,
      }));
    } else if (reportType === 'trend' && trendData) {
      name = `trend_${resource}_${trendData.period_start}_${trendData.period_end}`;
      rows = trendData.rows.map((r) => ({
        Period: r.bucket,
        Forecasted: r.forecasted,
        Available: r.available,
        Allocated: r.allocated,
      }));
    }
    if (!rows.length) {
      toast.error('Nothing to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    if (format === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${name}.csv`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');
      XLSX.writeFile(wb, `${name}.xlsx`);
    }
    toast.success(`Exported ${rows.length} rows`);
  };

  const resourceBtn = (val, Icon, lbl) => (
    <button
      data-testid={`resource-${val}`}
      onClick={() => setResource(val)}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
        resource === val ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
      }`}
    >
      <Icon className="h-4 w-4" />
      {lbl}
    </button>
  );

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="reports-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Reports</h1>
          <p className="text-sm text-slate-600 mt-2">Utilisation &amp; trend analysis across equipment and personnel</p>
        </div>
        <div className="flex gap-2">
          {resourceBtn('equipment', Truck, 'Equipment')}
          {resourceBtn('personnel', Users, 'Personnel')}
        </div>
      </div>

      {/* Report type tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {[
          ['utilization', 'Utilisation Report'],
          ['trend', 'Trend Report'],
        ].map(([val, lbl]) => (
          <button
            key={val}
            data-testid={`report-tab-${val}`}
            onClick={() => setReportType(val)}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              reportType === val
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200 shadow-sm mb-6">
        <CardContent className="pt-6">
          {reportType === 'utilization' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div>
                <FieldLabel>Timeframe</FieldLabel>
                <Select value={uTimeframe} onValueChange={setUTimeframe}>
                  <SelectTrigger data-testid="util-timeframe"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Reference date</FieldLabel>
                <input
                  type="date"
                  value={anchor}
                  onChange={(e) => setAnchor(e.target.value)}
                  data-testid="util-anchor"
                  className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm text-slate-700"
                />
              </div>
              <div>
                <FieldLabel>{catLabel}</FieldLabel>
                <Select value={uCategory} onValueChange={setUCategory}>
                  <SelectTrigger data-testid="util-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {catLabel}</SelectItem>
                    {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>{subLabel}</FieldLabel>
                <MultiSelect
                  label={subLabel}
                  options={subOptions.map((s) => ({ value: s, label: s }))}
                  selected={uSubcats}
                  onChange={setUSubcats}
                  testid="util-subcats"
                />
              </div>
              <div>
                <FieldLabel>{ownerLabel}</FieldLabel>
                <Select value={uOwner} onValueChange={setUOwner}>
                  <SelectTrigger data-testid="util-owner"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {ownerLabel}s</SelectItem>
                    {ownerOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Projects</FieldLabel>
                <MultiSelect
                  label="Projects"
                  options={(options.projects || []).map((p) => ({ value: p.id, label: p.name }))}
                  selected={uProjects}
                  onChange={setUProjects}
                  testid="util-projects"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div>
                <FieldLabel>Timeframe</FieldLabel>
                <Select value={tTimeframe} onValueChange={setTTimeframe}>
                  <SelectTrigger data-testid="trend-timeframe"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                    <SelectItem value="3year">3 Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>X-axis scale</FieldLabel>
                <Select value={tBucket} onValueChange={setTBucket}>
                  <SelectTrigger data-testid="trend-bucket"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Weeks</SelectItem>
                    <SelectItem value="month">Months</SelectItem>
                    <SelectItem value="quarter">Quarters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Reference date</FieldLabel>
                <input
                  type="date"
                  value={anchor}
                  onChange={(e) => setAnchor(e.target.value)}
                  data-testid="trend-anchor"
                  className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm text-slate-700"
                />
              </div>
              <div>
                <FieldLabel>{catLabel}</FieldLabel>
                <Select value={tCategory} onValueChange={setTCategory}>
                  <SelectTrigger data-testid="trend-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {catLabel}</SelectItem>
                    {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>{subLabel}</FieldLabel>
                <MultiSelect
                  label={subLabel}
                  options={subOptions.map((s) => ({ value: s, label: s }))}
                  selected={tSubcats}
                  onChange={setTSubcats}
                  testid="trend-subcats"
                />
              </div>
              <div>
                <FieldLabel>{ownerLabel}</FieldLabel>
                <Select value={tOwner} onValueChange={setTOwner}>
                  <SelectTrigger data-testid="trend-owner"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {ownerLabel}s</SelectItem>
                    {ownerOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Country</FieldLabel>
                <Select value={tCountry} onValueChange={setTCountry}>
                  <SelectTrigger data-testid="trend-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="outline" size="sm" onClick={() => exportData('csv')} data-testid="export-csv">
              <Download className="h-4 w-4 mr-1.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportData('xlsx')} data-testid="export-xlsx">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> XLSX
            </Button>
          </div>
        </CardContent>
      </Card>

      {reportType === 'utilization' ? (
        <UtilizationView data={utilData} loading={loading} subLabel={subLabel} resource={resource} />
      ) : (
        <TrendView data={trendData} loading={loading} resource={resource} />
      )}
    </div>
  );
}

function UtilizationView({ data, loading, subLabel, resource }) {
  const rows = data?.rows || [];
  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="utilization-chart">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
          {resource === 'equipment' ? 'Equipment' : 'Personnel'} Usage Rate &amp; Variance
        </CardTitle>
        {data && (
          <span className="text-xs font-mono text-slate-500">
            {data.window_start} → {data.window_end}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="util-empty">No data for the selected filters.</p>
        )}
        {!loading && rows.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={Math.max(320, rows.length * 34)}>
              <BarChart data={rows} layout="vertical" margin={{ left: 20, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" style={{ fontSize: '12px' }} unit="%" />
                <YAxis type="category" dataKey="label" width={150} stroke="#64748b" style={{ fontSize: '11px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                  formatter={(v, n, p) => [
                    `${v}%  (avail ${p.payload.available} · alloc ${p.payload.allocated} · var ${p.payload.variance})`,
                    'Usage Rate',
                  ]}
                />
                <ReferenceLine x={100} stroke="#ef4444" strokeDasharray="4 4" label={{ value: '100%', position: 'top', fontSize: 11, fill: '#ef4444' }} />
                <Bar dataKey="usage_rate" name="Usage Rate %" radius={[0, 3, 3, 0]}>
                  {rows.map((r, i) => (
                    <Cell key={i} fill={r.usage_rate > 100 ? '#ef4444' : r.usage_rate >= 70 ? '#f59e0b' : '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-mono text-xs uppercase text-slate-600">{subLabel.replace(/s$/, '')}</th>
                    <th className="text-right py-2 px-3 font-mono text-xs uppercase text-slate-600">Available</th>
                    <th className="text-right py-2 px-3 font-mono text-xs uppercase text-slate-600">Allocated (avg)</th>
                    <th className="text-right py-2 px-3 font-mono text-xs uppercase text-slate-600">Usage Rate</th>
                    <th className="text-right py-2 px-3 font-mono text-xs uppercase text-slate-600">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100" data-testid={`util-row-${idx}`}>
                      <td className="py-2 px-3">{item.label}</td>
                      <td className="text-right py-2 px-3 font-mono">{item.available}</td>
                      <td className="text-right py-2 px-3 font-mono">{item.allocated}</td>
                      <td className={`text-right py-2 px-3 font-mono font-medium ${item.usage_rate > 100 ? 'text-red-600' : 'text-slate-900'}`}>{item.usage_rate}%</td>
                      <td className={`text-right py-2 px-3 font-mono font-medium ${item.variance < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {item.variance > 0 ? '+' : ''}{item.variance}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-slate-400 mt-3">
                Variance = available − average allocated. Negative (red) = gap to fill / over-utilised; positive (green) = spare capacity.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TrendView({ data, loading, resource }) {
  const rows = data?.rows || [];
  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="trend-chart">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
          {resource === 'equipment' ? 'Equipment' : 'Personnel'} Trend — Forecasted / Available / Allocated
        </CardTitle>
        {data && (
          <span className="text-xs font-mono text-slate-500">
            {data.period_start} → {data.period_end}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="trend-empty">No data for the selected filters.</p>
        )}
        {!loading && rows.length > 0 && (
          <ResponsiveContainer width="100%" height={420}>
            <LineChart data={rows} margin={{ left: 10, right: 20, top: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="bucket" stroke="#64748b" style={{ fontSize: '11px' }} angle={-25} textAnchor="end" height={60} />
              <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '4px' }} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line type="monotone" dataKey="forecasted" name="Forecasted" stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="available" name="Available" stroke="#0f172a" strokeWidth={2} strokeDasharray="5 4" dot={false} />
              <Line type="monotone" dataKey="allocated" name="Allocated" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
